import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Lock, Loader2, LogOut, RefreshCw, MailCheck, ShieldAlert, DollarSign, Users, Receipt, Activity, ImagePlus, Trash2, Check, Sparkles, Ban, RotateCcw, Download, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, API, mutationPhotoUrl } from "../lib/api";

const STORAGE_KEY = "budgie_admin_v1";

function loadPwd() {
  try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={14} />
        <div className="font-mono text-[10px] uppercase tracking-widest">{label}</div>
      </div>
      <div className="mt-2 text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const tone =
    s === "paid" ? "bg-primary text-primary-foreground"
    : s === "initiated" || s === "open" ? "bg-secondary text-foreground"
    : s === "complete" ? "bg-primary/15 text-primary"
    : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>
      {status || "—"}
    </span>
  );
}

function PhotoCard({ item, onUpload, onDelete, busy }) {
  const fileRef = useRef(null);
  // Cache-bust the <img> whenever the underlying file changes — derived,
  // no extra state needed.
  const imgKey = `${item.id}-${item.has_real_photo ? "real" : "ai"}-${item.size_bytes || 0}`;

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(item.id, file);
    e.target.value = "";
  };

  return (
    <div className="border border-border bg-card overflow-hidden" data-testid={`photo-card-${item.id}`}>
      <div className="relative aspect-square bg-secondary/40">
        <img
          key={imgKey}
          src={`${mutationPhotoUrl(item.id)}?v=${item.size_bytes || "ai"}`}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {item.has_real_photo ? (
          <div className="absolute top-2 left-2">
            <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest" data-testid={`photo-badge-user-${item.id}`}>
              <Check size={10} /> User uploaded
            </span>
          </div>
        ) : null}
      </div>
      <div className="p-3 space-y-2">
        <div>
          <div className="overline text-[9px]">{item.category}</div>
          <div className="text-sm font-bold tracking-tight">{item.name}</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
            onChange={handlePick}
            className="hidden"
            data-testid={`photo-file-${item.id}`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid={`photo-upload-btn-${item.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 border border-border px-2 py-1.5 rounded-sm hover:bg-secondary text-xs disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
            {item.has_real_photo ? "Replace" : "Upload"}
          </button>
          {item.has_real_photo && (
            <button
              onClick={() => onDelete(item.id)}
              disabled={busy}
              data-testid={`photo-delete-btn-${item.id}`}
              className="inline-flex items-center justify-center border border-border px-2 py-1.5 rounded-sm hover:bg-destructive/10 hover:text-destructive text-xs disabled:opacity-60"
              title="Remove real photo, restore AI"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoManager({ headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [filter, setFilter] = useState("all"); // all | real | ai

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/photos", { headers: headers() });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load encyclopedia photos.");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    // Defer to next tick so the set-state inside load() doesn't trip
    // react-hooks/set-state-in-effect (mirrors the pattern used by loadAll).
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [load]);

  const handleUpload = async (mutationId, file) => {
    setBusyId(mutationId);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios_upload(`${API}/admin/photos/${mutationId}`, form, headers());
      toast.success(`Photo updated for ${mutationId}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed.");
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (mutationId) => {
    if (!window.confirm(`Remove the real photo for "${mutationId}" and fall back to AI generation?`)) return;
    setBusyId(mutationId);
    try {
      await api.delete(`/admin/photos/${mutationId}`, { headers: headers() });
      toast.success(`Restored AI fallback for ${mutationId}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusyId("");
    }
  };

  const visible = items.filter((i) =>
    filter === "all" ? true : filter === "real" ? i.has_real_photo : !i.has_real_photo
  );
  const counts = {
    real: items.filter((i) => i.has_real_photo).length,
    ai: items.filter((i) => !i.has_real_photo).length,
  };

  return (
    <div className="mt-14" data-testid="admin-photo-manager">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">// Encyclopedia photos</div>
          <h2 className="mt-2 text-base md:text-lg font-bold tracking-tight">Real-photo overrides</h2>
          <p className="mt-1 text-xs text-muted-foreground max-w-prose">
            Upload a real photo for any mutation to replace its AI-generated portrait. Real photos always beat AI. Square-cropped to 1024×1024 on save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setFilter("all")} data-testid="photo-filter-all"
            className={`border border-border px-2.5 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest ${filter === "all" ? "bg-foreground text-background" : "hover:bg-secondary"}`}>
            All <span className="opacity-60">({items.length})</span>
          </button>
          <button onClick={() => setFilter("real")} data-testid="photo-filter-real"
            className={`border border-border px-2.5 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest ${filter === "real" ? "bg-foreground text-background" : "hover:bg-secondary"}`}>
            Uploaded <span className="opacity-60">({counts.real})</span>
          </button>
          <button onClick={() => setFilter("ai")} data-testid="photo-filter-ai"
            className={`border border-border px-2.5 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest ${filter === "ai" ? "bg-foreground text-background" : "hover:bg-secondary"}`}>
            Default <span className="opacity-60">({counts.ai})</span>
          </button>
          <button onClick={load} disabled={loading} data-testid="photo-refresh"
            className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 rounded-sm hover:bg-secondary text-xs disabled:opacity-60">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-grid">
        {visible.map((it) => (
          <PhotoCard
            key={it.id}
            item={it}
            busy={busyId === it.id}
            onUpload={handleUpload}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
}

// Multipart upload helper — axios with explicit Content-Type omitted so the
// browser sets the correct boundary.
async function axios_upload(url, formData, extraHeaders) {
  return fetch(url, {
    method: "POST",
    headers: { ...extraHeaders },
    body: formData,
  }).then(async (r) => {
    if (!r.ok) {
      const body = await r.json().catch(() => ({ detail: r.statusText }));
      const err = new Error(body.detail || "Upload failed");
      err.response = { data: body };
      throw err;
    }
    return r.json();
  });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function DateRangeBar({ from, to, onChange, onClear, onExport, exporting }) {
  const presets = [
    { label: "7d", from: daysAgoISO(6), to: todayISO() },
    { label: "30d", from: daysAgoISO(29), to: todayISO() },
    { label: "90d", from: daysAgoISO(89), to: todayISO() },
    { label: "YTD", from: `${new Date().getUTCFullYear()}-01-01`, to: todayISO() },
  ];
  return (
    <div
      data-testid="admin-date-range"
      className="border border-border bg-card p-4 flex items-center gap-3 flex-wrap"
    >
      <Calendar size={14} className="text-muted-foreground" />
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Range</span>
      <input
        type="date"
        data-testid="admin-date-from"
        value={from || ""}
        onChange={(e) => onChange(e.target.value, to)}
        className="border border-border bg-background px-2 py-1.5 rounded-sm text-xs font-mono"
      />
      <span className="text-xs text-muted-foreground">to</span>
      <input
        type="date"
        data-testid="admin-date-to"
        value={to || ""}
        onChange={(e) => onChange(from, e.target.value)}
        className="border border-border bg-background px-2 py-1.5 rounded-sm text-xs font-mono"
      />
      <div className="flex items-center gap-1 ml-1">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            data-testid={`admin-preset-${p.label}`}
            onClick={() => onChange(p.from, p.to)}
            className="border border-border px-2 py-1 rounded-sm text-[11px] font-mono uppercase hover:bg-secondary"
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          data-testid="admin-preset-all"
          onClick={onClear}
          className="border border-border px-2 py-1 rounded-sm text-[11px] font-mono uppercase hover:bg-secondary"
        >
          All
        </button>
      </div>
      <button
        type="button"
        onClick={onExport}
        disabled={exporting}
        data-testid="admin-export-csv"
        className="ml-auto inline-flex items-center gap-1.5 border border-border px-3 py-1.5 rounded-sm text-xs hover:bg-secondary disabled:opacity-60"
      >
        {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
        Export CSV
      </button>
    </div>
  );
}

function RevenueChart({ series }) {
  // Inline SVG bar chart — no deps. Renders zeros for missing days so
  // sparse data still shows a continuous timeline.
  const data = useMemo(() => {
    if (!series || series.length === 0) return [];
    // Fill gaps so the x-axis is continuous
    const out = [];
    const first = new Date(`${series[0].date}T00:00:00Z`);
    const last = new Date(`${series[series.length - 1].date}T00:00:00Z`);
    const byDate = Object.fromEntries(series.map((s) => [s.date, s]));
    for (let d = new Date(first); d <= last; d.setUTCDate(d.getUTCDate() + 1)) {
      const key = d.toISOString().slice(0, 10);
      const row = byDate[key];
      out.push({ date: key, revenue: row?.revenue || 0, count: row?.count || 0 });
    }
    return out;
  }, [series]);

  const max = Math.max(1, ...data.map((d) => d.revenue));
  const total = data.reduce((s, d) => s + d.revenue, 0);
  const totalCount = data.reduce((s, d) => s + d.count, 0);

  if (data.length === 0) {
    return (
      <div className="border border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="admin-revenue-chart-empty">
        No paid transactions in the selected range.
      </div>
    );
  }

  const width = 800;
  const height = 220;
  const padTop = 14;
  const padBottom = 28;
  const padLeft = 72;
  const padRight = 14;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const barW = Math.max(2, chartW / data.length - 2);

  return (
    <div className="border border-border bg-card p-5" data-testid="admin-revenue-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">// Revenue series</div>
          <div className="mt-1 text-2xl font-black tracking-tight">A${total.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {totalCount} paid · {data.length} day{data.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Y-axis grid */}
        {[0, 0.5, 1].map((t) => {
          const y = padTop + chartH * (1 - t);
          return (
            <g key={t}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} stroke="currentColor" strokeOpacity="0.12" />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" fontSize="11" className="fill-muted-foreground" fontFamily="monospace">
                {`A$${(max * t).toFixed(max < 5 ? 2 : 0)}`}
              </text>
            </g>
          );
        })}
        {/* Bars */}
        {data.map((d, i) => {
          const h = (d.revenue / max) * chartH;
          const x = padLeft + i * (chartW / data.length) + 1;
          const y = padTop + (chartH - h);
          return (
            <g key={d.date}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                className={d.revenue > 0 ? "fill-primary" : "fill-muted"}
                opacity={d.revenue > 0 ? 0.85 : 0.15}
                data-testid={`admin-chart-bar-${d.date}`}
              >
                <title>{`${d.date}: A$${d.revenue.toFixed(2)} (${d.count} paid)`}</title>
              </rect>
            </g>
          );
        })}
        {/* X-axis labels (first, middle, last) */}
        {[0, Math.floor(data.length / 2), data.length - 1].filter((v, i, a) => a.indexOf(v) === i && v >= 0).map((i) => {
          const x = padLeft + i * (chartW / data.length) + barW / 2;
          return (
            <text key={i} x={x} y={height - 8} textAnchor="middle" fontSize="11" className="fill-muted-foreground" fontFamily="monospace">
              {data[i].date.slice(5)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export default function Admin() {
  const [pwd, setPwd] = useState(loadPwd());
  const [authed, setAuthed] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [pwdInput, setPwdInput] = useState("");
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendingFor, setResendingFor] = useState("");
  const [revokingFor, setRevokingFor] = useState("");
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const headers = useCallback(() => ({ "X-Admin-Password": pwd }), [pwd]);

  const rangeQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("from", dateFrom);
    if (dateTo) p.set("to", dateTo);
    const s = p.toString();
    return s ? `&${s}` : "";
  }, [dateFrom, dateTo]);

  const loadAll = useCallback(async () => {
    if (!pwd) return;
    setLoading(true);
    try {
      const [s, p, rs] = await Promise.all([
        api.get(`/admin/stats?_=1${rangeQuery}`, { headers: headers() }),
        api.get(`/admin/purchases?limit=100${rangeQuery}`, { headers: headers() }),
        api.get(`/admin/revenue-series?_=1${rangeQuery}`, { headers: headers() }),
      ]);
      setStats(s.data);
      setPurchases(p.data.items || []);
      setSeries(rs.data.series || []);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        setPwd("");
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        toast.error("Session expired — please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, [pwd, headers, rangeQuery]);

  useEffect(() => {
    // Defer loadAll out of the effect tick so the synchronous setState
    // calls inside don't trip react-hooks/set-state-in-effect.
    const t = setTimeout(loadAll, 0);
    return () => clearTimeout(t);
  }, [loadAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await api.post("/admin/login", { password: pwdInput });
      try { localStorage.setItem(STORAGE_KEY, pwdInput); } catch { /* ignore */ }
      setPwd(pwdInput);
      setPwdInput("");
      setAuthed(true);
      toast.success("Welcome, admin.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setPwd("");
    setAuthed(false);
    setStats(null);
    setPurchases([]);
  };

  const handleResend = async (email) => {
    setResendingFor(email);
    try {
      const { data } = await api.post(
        "/admin/resend-license",
        { email },
        { headers: headers() }
      );
      toast.success(data.sent ? `Re-sent to ${email}` : `License found but email dispatch failed for ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend.");
    } finally {
      setResendingFor("");
    }
  };

  const handleRevoke = async (email, isRevoked) => {
    const verb = isRevoked ? "un-revoke" : "revoke";
    if (!window.confirm(
      isRevoked
        ? `Un-revoke ${email}'s Pro license? They will regain Pro access immediately.`
        : `Revoke ${email}'s Pro license? They will lose Pro access immediately. The license key stays on record and can be un-revoked.`
    )) return;
    setRevokingFor(email);
    try {
      const path = isRevoked ? "/admin/unrevoke-license" : "/admin/revoke-license";
      await api.post(path, { email }, { headers: headers() });
      toast.success(`${verb === "revoke" ? "Revoked" : "Un-revoked"} ${email}`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not ${verb}.`);
    } finally {
      setRevokingFor("");
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);
      const resp = await api.get(`/admin/export.csv?${params.toString()}`, {
        headers: headers(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `budgie-transactions-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="overline">// Admin</div>
        <h1 className="mt-3 text-3xl font-black tracking-tight">Restricted</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Enter the admin password to view revenue, customers, and purchases.
        </p>
        <form onSubmit={handleLogin} className="mt-6 space-y-3" data-testid="admin-login-form">
          <label className="block">
            <div className="overline mb-1">Password</div>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password"
                required
                autoFocus
                value={pwdInput}
                onChange={(e) => setPwdInput(e.target.value)}
                data-testid="admin-password-input"
                className="w-full border border-border bg-background pl-9 pr-3 py-2.5 text-sm font-mono rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </label>
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background px-4 py-2.5 rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {loggingIn ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
            {loggingIn ? "Verifying…" : "Enter dashboard"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">// Admin</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Dashboard</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Live ledger of Stripe transactions and Pro licenses.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAll}
            disabled={loading}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-border px-3 py-2 rounded-sm hover:bg-secondary text-sm disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-border px-3 py-2 rounded-sm hover:bg-secondary text-sm"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testid="admin-stat-revenue"
          icon={DollarSign}
          label="Revenue in range"
          value={stats ? `A$${stats.total_revenue_aud.toFixed(2)}` : "—"}
        />
        <StatCard
          testid="admin-stat-customers"
          icon={Users}
          label="Pro customers (lifetime)"
          value={stats?.total_customers ?? "—"}
        />
        <StatCard
          testid="admin-stat-paid"
          icon={Receipt}
          label="Paid in range"
          value={stats?.paid_transactions ?? "—"}
        />
        <StatCard
          testid="admin-stat-open"
          icon={Activity}
          label="Open checkouts"
          value={stats?.open_checkouts ?? "—"}
        />
      </div>

      {/* Date range + CSV export bar */}
      <div className="mt-6">
        <DateRangeBar
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => { setDateFrom(f || ""); setDateTo(t || ""); }}
          onClear={() => { setDateFrom(""); setDateTo(""); }}
          onExport={handleExportCsv}
          exporting={exporting}
        />
      </div>

      {/* Revenue chart */}
      <div className="mt-6">
        <RevenueChart series={series} />
      </div>

      {/* Purchases table */}
      <div className="mt-12">
        <div className="overline">// Recent purchases</div>
        <h2 className="mt-2 text-base md:text-lg font-bold tracking-tight">Transactions</h2>
        <div className="mt-4 border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-purchases-table">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Amount</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Payment</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">License</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No transactions in range.</td></tr>
              )}
              {purchases.map((p, i) => {
                const date = p.created_at ? new Date(p.created_at).toLocaleString() : "—";
                const hasLicense = !!p.license_key;
                const isRevoked = !!p.revoked_at;
                return (
                  <tr key={p.session_id || i} data-testid={`admin-purchase-row-${i}`} className={`border-b border-border last:border-b-0 hover:bg-secondary/30 ${isRevoked ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{date}</td>
                    <td className="px-4 py-3 font-mono text-xs break-all">{p.email || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">A${(p.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusPill status={p.payment_status} /></td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {hasLicense ? (
                        <div className="flex items-center gap-2">
                          <span className="break-all">{p.license_key}</span>
                          {isRevoked && (
                            <span
                              data-testid={`admin-revoked-pill-${i}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase font-mono tracking-wider px-1.5 py-0.5 bg-destructive/10 text-destructive rounded-sm"
                            >
                              <Ban size={9} /> Revoked
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {hasLicense ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleResend(p.email)}
                            disabled={resendingFor === p.email || isRevoked}
                            data-testid={`admin-resend-${i}`}
                            className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 rounded-sm hover:bg-secondary text-xs disabled:opacity-40"
                            title={isRevoked ? "Cannot resend a revoked license" : "Re-send license email"}
                          >
                            {resendingFor === p.email ? <Loader2 size={12} className="animate-spin" /> : <MailCheck size={12} />}
                            Re-send
                          </button>
                          <button
                            onClick={() => handleRevoke(p.email, isRevoked)}
                            disabled={revokingFor === p.email}
                            data-testid={`admin-revoke-${i}`}
                            className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 rounded-sm text-xs disabled:opacity-60 ${
                              isRevoked
                                ? "border-border hover:bg-secondary"
                                : "border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            }`}
                            title={isRevoked ? "Un-revoke" : "Revoke"}
                          >
                            {revokingFor === p.email
                              ? <Loader2 size={12} className="animate-spin" />
                              : isRevoked ? <RotateCcw size={12} /> : <Ban size={12} />}
                            {isRevoked ? "Un-revoke" : "Revoke"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-xs text-muted-foreground font-mono">
          {purchases.length} of {stats?.paid_transactions != null ? `${stats.paid_transactions + (stats.open_checkouts || 0)} total` : ""} shown · LIVE Stripe mode
        </div>
      </div>

      <PhotoManager headers={headers} />
    </div>
  );
}
